import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, Product } from '@/templates/TemplateInterface';
import { toast } from 'sonner';
import { getCartLineSubtotal, wholesaleConfigForCart } from '@/utils/wholesalePricing';

interface EnhancedCartContextType {
  items: CartItem[];
  addToCart: (product: Product, quantity?: number, options?: Record<string, string>, variantId?: string, variantName?: string, variantAttributes?: Record<string, string>) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getCount: () => number;
  
  // Enhanced features
  saveCartForLater: () => void;
  restoreCart: () => void;
  hasItemInCart: (productId: string, options?: Record<string, string>) => boolean;
  getItemQuantity: (productId: string, options?: Record<string, string>) => number;
  getItemId: (item: CartItem) => string;
  
  // Local wishlist (for guest users)
  localWishlist: string[];
  addToLocalWishlist: (productId: string) => void;
  removeFromLocalWishlist: (productId: string) => void;
  isInLocalWishlist: (productId: string) => boolean;
  clearLocalWishlist: () => void;
}

const EnhancedCartContext = createContext<EnhancedCartContextType | undefined>(undefined);

export function EnhancedCartProvider({ children, storeUrl }: { children: ReactNode; storeUrl?: string }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [localWishlist, setLocalWishlist] = useState<string[]>([]);

  const cartKey = storeUrl ? `cart_${storeUrl}` : 'cart';
  const wishlistKey = storeUrl ? `local_wishlist_${storeUrl}` : 'local_wishlist';

  // Load cart and wishlist from localStorage on mount
  useEffect(() => {
    try {
      // Load cart
      const savedCart = localStorage.getItem(cartKey);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Validate cart structure
        if (Array.isArray(parsedCart)) {
          setItems(parsedCart);
        }
      } else {
        // Migration: if no enhanced cart found but a legacy cart exists under 'cart', import it
        if (cartKey !== 'cart') {
          const legacyCartRaw = localStorage.getItem('cart');
          if (legacyCartRaw) {
            try {
              const legacyCart = JSON.parse(legacyCartRaw);
              if (Array.isArray(legacyCart) && legacyCart.length > 0) {
                setItems(legacyCart);
                // Persist under namespaced key; keep legacy copy for other pages still using it
                localStorage.setItem(cartKey, JSON.stringify(legacyCart));
              }
            } catch (err) {
              console.error('Failed to parse legacy cart during migration:', err);
            }
          }
        }
      }

      // Load local wishlist
      const savedWishlist = localStorage.getItem(wishlistKey);
      if (savedWishlist) {
        const parsedWishlist = JSON.parse(savedWishlist);
        if (Array.isArray(parsedWishlist)) {
          setLocalWishlist(parsedWishlist);
        }
      }
    } catch (error) {
      console.error('Failed to parse saved cart/wishlist data:', error);
      // Clear corrupted data
      localStorage.removeItem(cartKey);
      localStorage.removeItem(wishlistKey);
    }
  }, [cartKey, wishlistKey]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(cartKey, JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
      toast.error('Failed to save cart. Please check your storage settings.');
    }
  }, [items, cartKey]);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(wishlistKey, JSON.stringify(localWishlist));
    } catch (error) {
      console.error('Failed to save wishlist to localStorage:', error);
    }
  }, [localWishlist, wishlistKey]);

  // Generate unique item ID based on product and options
  const generateItemId = (productId: string, options?: Record<string, string>, variantId?: string) => {
    const optionsStr = options ? JSON.stringify(options) : '';
    const variantStr = variantId || '';
    return `${productId}_${optionsStr}_${variantStr}`;
  };

  // Helper function to get itemId for an existing cart item
  const getItemId = (item: CartItem) => {
    return generateItemId(item.productId, item.options, item.variantId);
  };

  const getRetailUnitPrice = (
    product: Product,
    options?: Record<string, string>,
    variantSku?: string
  ): number => {
    let price = product.price;
    if (variantSku && product.variants?.length) {
      const v = product.variants.find((x) => x.sku === variantSku);
      if (v) price = v.price;
    }
    if (options && product.options && Array.isArray(product.options)) {
      Object.entries(options).forEach(([optionName, optionValue]) => {
        const option = product.options.find((opt) => opt.name === optionName);
        const value = option?.values.find((val) => val.name === optionValue);
        if (value && typeof value.price_adjustment === 'number') {
          price += value.price_adjustment;
        }
      });
    }
    return price;
  };

  const addToCart = (
    product: Product, 
    quantity: number = 1, 
    options?: Record<string, string>,
    variantId?: string,
    variantName?: string,
    variantAttributes?: Record<string, string>
  ) => {
    try {
      const retailUnit = getRetailUnitPrice(product, options, variantId);
      const wholesale = wholesaleConfigForCart(product, variantId);
      const itemId = generateItemId(product.id, options, variantId);
      
      // Check if the item already exists in the cart
      const existingItemIndex = items.findIndex(item => {
        const existingItemId = generateItemId(item.productId, item.options, item.variantId);
        return existingItemId === itemId;
      });

      if (existingItemIndex !== -1) {
        // If the item already exists, update its quantity
        const updatedItems = [...items];
        updatedItems[existingItemIndex].quantity += quantity;
        setItems(updatedItems);
        toast.success(`Updated ${product.name} quantity in cart`);
      } else {
        // If it's a new item, add it to the cart
        const newItem: CartItem = {
          productId: product.id,
          variantId: variantId,
          variantName: variantName,
          variantAttributes: variantAttributes,
          name: product.name,
          price: retailUnit,
          wholesale: wholesale ?? undefined,
          quantity,
          options,
          image: product.images?.[0]?.url,
        };
        setItems(prev => [...prev, newItem]);
        toast.success(`Added ${product.name} to cart`);
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const removeFromCart = (itemId: string) => {
    setItems(items.filter(item => {
      const existingItemId = generateItemId(item.productId, item.options, item.variantId);
      return existingItemId !== itemId;
    }));
    toast.success('Item removed from cart');
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setItems(
      items.map(item => {
        const existingItemId = generateItemId(item.productId, item.options, item.variantId);
        return existingItemId === itemId ? { ...item, quantity } : item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(cartKey);
    toast.success('Cart cleared');
  };

  const getTotal = () => {
    return items.reduce((total, item) => total + getCartLineSubtotal(item), 0);
  };

  const getCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  const saveCartForLater = () => {
    try {
      const savedCartKey = `${cartKey}_saved`;
      localStorage.setItem(savedCartKey, JSON.stringify(items));
      toast.success('Cart saved for later');
    } catch (error) {
      console.error('Error saving cart for later:', error);
      toast.error('Failed to save cart');
    }
  };

  const restoreCart = () => {
    try {
      const savedCartKey = `${cartKey}_saved`;
      const savedCart = localStorage.getItem(savedCartKey);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          setItems(parsedCart);
          localStorage.removeItem(savedCartKey);
          toast.success('Cart restored');
        }
      }
    } catch (error) {
      console.error('Error restoring cart:', error);
      toast.error('Failed to restore cart');
    }
  };

  const hasItemInCart = (productId: string, options?: Record<string, string>) => {
    const itemId = generateItemId(productId, options);
    return items.some(item => {
      const existingItemId = generateItemId(item.productId, item.options);
      return existingItemId === itemId;
    });
  };

  const getItemQuantity = (productId: string, options?: Record<string, string>) => {
    const itemId = generateItemId(productId, options);
    const item = items.find(item => {
      const existingItemId = generateItemId(item.productId, item.options);
      return existingItemId === itemId;
    });
    return item?.quantity || 0;
  };

  // Local wishlist methods (for guest users)
  const addToLocalWishlist = (productId: string) => {
    if (!localWishlist.includes(productId)) {
      setLocalWishlist(prev => [...prev, productId]);
      toast.success('Added to wishlist');
    }
  };

  const removeFromLocalWishlist = (productId: string) => {
    setLocalWishlist(prev => prev.filter(id => id !== productId));
    toast.success('Removed from wishlist');
  };

  const isInLocalWishlist = (productId: string) => {
    return localWishlist.includes(productId);
  };

  const clearLocalWishlist = () => {
    setLocalWishlist([]);
    localStorage.removeItem(wishlistKey);
  };

  return (
    <EnhancedCartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getCount,
        saveCartForLater,
        restoreCart,
        hasItemInCart,
        getItemQuantity,
        getItemId,
        localWishlist,
        addToLocalWishlist,
        removeFromLocalWishlist,
        isInLocalWishlist,
        clearLocalWishlist,
      }}
    >
      {children}
    </EnhancedCartContext.Provider>
  );
}

export function useEnhancedCart() {
  const context = useContext(EnhancedCartContext);
  if (context === undefined) {
    throw new Error('useEnhancedCart must be used within an EnhancedCartProvider');
  }
  return context;
}

// Alias for backward compatibility
export const useCart = useEnhancedCart;
